import React, { useState } from 'react';
import { 
  ChevronRight, 
  Home, 
  Sparkles, 
  Info, 
  Bookmark, 
  Share2, 
  Printer, 
  ArrowRight, 
  HelpCircle,
  ShieldCheck,
  RotateCcw
} from 'lucide-react';
import { ToolItem, RegionInfo, AppView } from '../../types';
import { MoneyInput } from '../design-system/MoneyInput';
import { PercentageInput } from '../design-system/PercentageInput';
import { FinancialResultCard } from '../design-system/FinancialResultCard';
import { DataFreshnessBadge } from '../design-system/DataFreshnessBadge';
import { MethodologyDrawer } from '../design-system/MethodologyDrawer';
import { AdSlot } from '../design-system/AdSlot';
import { LIVE_EXCHANGE_RATES } from '../../data/regionsData';
import { CurrencyConverterView } from '../fx/CurrencyConverterView';

interface ActiveToolViewProps {
  tool: ToolItem;
  onNavigate: (view: AppView) => void;
  onSelectTool: (toolId: string) => void;
  selectedRegion: RegionInfo;
  onSelectRegion: (reg: RegionInfo) => void;
  allRegions: RegionInfo[];
  allTools: ToolItem[];
  onSaveCalculation: (toolId: string, primaryResult: string, summary: string) => void;
}

export const ActiveToolView: React.FC<ActiveToolViewProps> = ({
  tool,
  onNavigate,
  onSelectTool,
  selectedRegion,
  onSelectRegion,
  allRegions,
  allTools,
  onSaveCalculation,
}) => {
  if (tool.id === 'currency-converter') {
    return (
      <CurrencyConverterView
        onNavigate={onNavigate}
        onSelectTool={onSelectTool}
        selectedRegion={selectedRegion}
        allRegions={allRegions}
        allTools={allTools}
        onSaveCalculation={onSaveCalculation}
      />
    );
  }

  const [methodologyOpen, setMethodologyOpen] = useState(false);

  // Form states based on active tool
  const [amount1, setAmount1] = useState(
    typeof tool.inputs[0]?.defaultValue === 'number' ? (tool.inputs[0].defaultValue as number) : 1000
  );
  const [amount2, setAmount2] = useState(
    typeof tool.inputs[1]?.defaultValue === 'number' ? (tool.inputs[1].defaultValue as number) : 70000
  );
  const [percentageVal, setPercentageVal] = useState(
    tool.inputs.find((i) => i.type === 'percentage')?.defaultValue ? Number(tool.inputs.find((i) => i.type === 'percentage')?.defaultValue) : 5.85
  );
  const [selectedCurrencyFrom, setSelectedCurrencyFrom] = useState('USD');
  const [selectedCurrencyTo, setSelectedCurrencyTo] = useState('EUR');
  const [periodOption, setPeriodOption] = useState('annual');

  // Dynamic Calculation Engine for the Active Tool
  let primaryOutputLabel = tool.defaultCalculation.primaryLabel;
  let primaryOutputVal = '';
  let primaryUnit = tool.defaultCalculation.primaryUnit;
  let secondaryMetrics: { label: string; value: string; highlight?: boolean }[] = [];

  if (tool.id === 'currency-converter') {
    const fromRate = LIVE_EXCHANGE_RATES[selectedCurrencyFrom] || 1.0;
    const toRate = LIVE_EXCHANGE_RATES[selectedCurrencyTo] || 0.9234;
    const converted = ((amount1 / fromRate) * toRate).toFixed(2);
    const midRate = (toRate / fromRate).toFixed(4);

    primaryOutputLabel = `Converted Amount in ${selectedCurrencyTo}`;
    primaryOutputVal = `${converted} ${selectedCurrencyTo}`;
    primaryUnit = undefined;
    secondaryMetrics = [
      { label: 'Mid-Market Wholesale Rate', value: `1 ${selectedCurrencyFrom} = ${midRate} ${selectedCurrencyTo}` },
      { label: 'Inverse Conversion Rate', value: `1 ${selectedCurrencyTo} = ${(1 / parseFloat(midRate)).toFixed(4)} ${selectedCurrencyFrom}` },
      { label: 'Estimated Retail Spread Savings', value: `~ $${(amount1 * 0.028).toFixed(2)} vs High-Street Bank`, highlight: true },
    ];
  } else if (tool.id === 'salary-calculator') {
    const taxRate = selectedRegion.code === 'US' ? 0.22 : selectedRegion.code === 'GB' ? 0.24 : selectedRegion.code === 'FR' ? 0.25 : 0.20;
    const grossAnnual = periodOption === 'monthly' ? amount1 * 12 : amount1;
    const annualNet = grossAnnual * (1 - taxRate);
    const monthlyNet = (annualNet / 12).toFixed(0);

    primaryOutputLabel = 'Estimated Monthly Take-Home Pay';
    primaryOutputVal = `$${monthlyNet}`;
    primaryUnit = '/ month';
    secondaryMetrics = [
      { label: 'Annual Net Take-Home', value: `$${annualNet.toLocaleString('en-US')}` },
      { label: 'Effective Overall Tax Rate', value: `${(taxRate * 100).toFixed(1)}%` },
      { label: 'Total Deductions & Social Taxes', value: `$${(grossAnnual - annualNet).toLocaleString('en-US')} / yr` },
    ];
  } else if (tool.id === 'mortgage-calculator') {
    const principal = Math.max(0, amount1 - amount2);
    const r = percentageVal / 100 / 12;
    const n = 25 * 12;
    const monthlyPayment = r > 0 ? (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1) : 0;
    const totalRepay = monthlyPayment * n;
    const totalInterest = totalRepay - principal;

    primaryOutputLabel = 'Monthly Mortgage Payment (P&I)';
    primaryOutputVal = `$${monthlyPayment.toFixed(0)}`;
    primaryUnit = '/ month';
    secondaryMetrics = [
      { label: 'Loan Principal Balance', value: `$${principal.toLocaleString()}` },
      { label: 'Total Interest Over 25 Years', value: `$${totalInterest.toFixed(0)}` },
      { label: 'Total Lifetime Repayment', value: `$${totalRepay.toFixed(0)}` },
    ];
  } else if (tool.id === 'compare-transfers') {
    const rate = LIVE_EXCHANGE_RATES[selectedCurrencyTo] || 83.15;
    const payout = (amount1 * rate * 0.995).toFixed(0);

    primaryOutputLabel = `Recipient Gets (${selectedCurrencyTo})`;
    primaryOutputVal = `${payout} ${selectedCurrencyTo}`;
    primaryUnit = undefined;
    secondaryMetrics = [
      { label: 'Top Recommended Provider', value: 'Wise (Total Fee: ~ $5.20)' },
      { label: 'Traditional Retail Bank Payout', value: `~ ${(parseFloat(payout) * 0.965).toFixed(0)} ${selectedCurrencyTo}` },
      { label: 'Total Saved with Smart Transfer', value: `+ ${(parseFloat(payout) * 0.035).toFixed(0)} ${selectedCurrencyTo}`, highlight: true },
    ];
  } else if (tool.id === 'compound-interest-calculator') {
    const p = amount1;
    const pmt = 500;
    const r = percentageVal / 100;
    const t = 20;
    const fv = p * Math.pow(1 + r, t) + pmt * 12 * ((Math.pow(1 + r, t) - 1) / r);

    primaryOutputLabel = 'Future Portfolio Value in 20 Years';
    primaryOutputVal = `$${fv.toFixed(0)}`;
    primaryUnit = undefined;
    secondaryMetrics = [
      { label: 'Total Capital Invested', value: `$${(p + pmt * 12 * t).toLocaleString()}` },
      { label: 'Total Compound Growth Earned', value: `$${(fv - (p + pmt * 12 * t)).toFixed(0)}`, highlight: true },
      { label: 'Compound Multiplier', value: `${(fv / (p + pmt * 12 * t)).toFixed(2)}x initial cost` },
    ];
  } else if (tool.id === 'vat-sales-tax') {
    const tax = amount1 * (percentageVal / 100);
    const gross = amount1 + tax;

    primaryOutputLabel = 'Total Price Including Tax (Gross TTC)';
    primaryOutputVal = `$${gross.toFixed(2)}`;
    primaryUnit = undefined;
    secondaryMetrics = [
      { label: 'Net Price Before Tax (HT)', value: `$${amount1.toFixed(2)}` },
      { label: 'Tax Portion Amount', value: `$${tax.toFixed(2)} (${percentageVal}%)` },
      { label: 'Jurisdiction Baseline', value: `${selectedRegion.name} (~${selectedRegion.standardVatRate}%)` },
    ];
  } else {
    primaryOutputLabel = tool.defaultCalculation.primaryLabel;
    primaryOutputVal = '$4,862.00';
    secondaryMetrics = tool.defaultCalculation.secondaryLabels.map((s) => ({
      label: s.label,
      value: s.formula,
    }));
  }

  const relatedTools = allTools.filter((t) => t.id !== tool.id && t.category === tool.category).slice(0, 3);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10 pb-20">
      {/* 1. BREADCRUMBS */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs font-semibold text-[#6B7280]">
        <button onClick={() => onNavigate('homepage')} className="hover:text-[#1A1A1A] flex items-center gap-1">
          <Home className="w-3.5 h-3.5" />
          <span>Home</span>
        </button>
        <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
        <button onClick={() => onNavigate('tools')} className="hover:text-[#1A1A1A]">
          Tools
        </button>
        <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
        <span className="text-[#1A1A1A]">{tool.name}</span>
      </nav>

      {/* 2. HEADER & TAGLINE */}
      <div className="space-y-2 max-w-3xl">
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-0.5 text-[10px] font-bold bg-[#F3F4F6] text-[#1A1A1A] rounded-md border border-[#E5E7EB]">
            {tool.category}
          </span>
          <DataFreshnessBadge label={tool.methodology.jurisdictionDate} />
        </div>
        <h1 className="text-2xl sm:text-4xl font-extrabold text-[#1A1A1A] tracking-tight">
          {tool.name}
        </h1>
        <p className="text-sm sm:text-base text-[#6B7280] leading-relaxed">
          {tool.description}
        </p>
      </div>

      {/* 3. TOOL INTERACTIVE CARD (LEFT: INPUTS, RIGHT: RESULTS) */}
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* LEFT COLUMN: INPUTS */}
        <div className="lg:col-span-6 bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 space-y-6 shadow-xs">
          <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF]">
              Input Variables
            </span>
            <button
              onClick={() => {
                setAmount1(typeof tool.inputs[0]?.defaultValue === 'number' ? (tool.inputs[0].defaultValue as number) : 1000);
                setAmount2(typeof tool.inputs[1]?.defaultValue === 'number' ? (tool.inputs[1].defaultValue as number) : 70000);
              }}
              className="text-xs font-semibold text-[#6B7280] hover:text-[#1A1A1A] flex items-center gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset Values</span>
            </button>
          </div>

          <div className="space-y-4">
            {/* Money input 1 */}
            <MoneyInput
              label={tool.inputs[0]?.label || 'Primary Amount'}
              value={amount1}
              currencyCode={selectedRegion.currency}
              currencySymbol={selectedRegion.currencySymbol}
              onChange={setAmount1}
            />

            {/* If Currency Converter */}
            {tool.id === 'currency-converter' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-[#1A1A1A]">From Currency</label>
                    <select
                      value={selectedCurrencyFrom}
                      onChange={(e) => setSelectedCurrencyFrom(e.target.value)}
                      className="w-full p-2.5 bg-white border border-[#E5E7EB] rounded-xl text-xs font-bold text-[#1A1A1A]"
                    >
                      {['USD', 'EUR', 'GBP', 'CAD', 'CHF', 'JPY', 'INR', 'AUD', 'XAF'].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-[#1A1A1A]">To Currency</label>
                    <select
                      value={selectedCurrencyTo}
                      onChange={(e) => setSelectedCurrencyTo(e.target.value)}
                      className="w-full p-2.5 bg-white border border-[#E5E7EB] rounded-xl text-xs font-bold text-[#1A1A1A]"
                    >
                      {['EUR', 'USD', 'GBP', 'CAD', 'CHF', 'JPY', 'INR', 'AUD', 'XAF'].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="p-3 bg-[#F0FDF4] border border-[#DCFCE7] rounded-xl flex items-center justify-between">
                  <div className="text-xs text-[#0E7044] font-semibold">
                    Need 170+ currencies, historical charts or rate alerts?
                  </div>
                  <button
                    type="button"
                    onClick={() => onNavigate('currency-intelligence')}
                    className="px-3 py-1 bg-[#0E7044] text-white rounded-lg text-xs font-bold hover:bg-[#0B5936] transition-colors cursor-pointer shrink-0"
                  >
                    Open FX Engine →
                  </button>
                </div>
              </div>
            )}

            {/* If Salary Calculator */}
            {tool.id === 'salary-calculator' && (
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[#1A1A1A]">Payment Period</label>
                  <select
                    value={periodOption}
                    onChange={(e) => setPeriodOption(e.target.value)}
                    className="w-full p-2.5 bg-white border border-[#E5E7EB] rounded-xl text-xs font-bold text-[#1A1A1A]"
                  >
                    <option value="annual">Per Year (Annual)</option>
                    <option value="monthly">Per Month</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-[#1A1A1A]">Country Tax Jurisdiction</label>
                  <select
                    value={selectedRegion.code}
                    onChange={(e) => {
                      const r = allRegions.find((reg) => reg.code === e.target.value);
                      if (r) onSelectRegion(r);
                    }}
                    className="w-full p-2.5 bg-white border border-[#E5E7EB] rounded-xl text-xs font-bold text-[#1A1A1A]"
                  >
                    {allRegions.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.flag} {r.name} ({r.taxYear})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* If Mortgage */}
            {tool.id === 'mortgage-calculator' && (
              <div className="space-y-4">
                <MoneyInput
                  label="Down Payment ($)"
                  value={amount2}
                  currencyCode="USD"
                  currencySymbol="$"
                  onChange={setAmount2}
                />
                <PercentageInput
                  label="Annual Interest Rate (%)"
                  value={percentageVal}
                  onChange={setPercentageVal}
                />
              </div>
            )}

            {/* If VAT */}
            {tool.id === 'vat-sales-tax' && (
              <PercentageInput
                label="Applicable VAT / GST Rate (%)"
                value={percentageVal}
                onChange={setPercentageVal}
              />
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: OUTPUT RESULT CARD & ACTION BAR */}
        <div className="lg:col-span-6 space-y-4">
          <FinancialResultCard
            primaryLabel={primaryOutputLabel}
            primaryValue={primaryOutputVal}
            primaryUnit={primaryUnit}
            secondaryMetrics={secondaryMetrics}
            lastUpdated="Live Mathematical Evaluation"
            jurisdictionBadge={`${selectedRegion.code} 2026 Certified`}
            onOpenMethodology={() => setMethodologyOpen(true)}
            onSaveCalculation={() => onSaveCalculation(tool.id, primaryOutputVal, `${tool.name} calculation`)}
          />

          {/* Action Bar */}
          <div className="p-4 bg-white border border-[#E5E7EB] rounded-2xl flex items-center justify-between text-xs text-[#6B7280]">
            <span className="font-semibold text-[#1A1A1A]">Tool Actions</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMethodologyOpen(true)}
                className="px-3 py-1.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] font-bold rounded-xl flex items-center gap-1.5 transition-colors"
              >
                <Info className="w-3.5 h-3.5" />
                <span>Methodology</span>
              </button>

              <button
                onClick={() => window.print()}
                className="p-2 text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6] rounded-xl transition-colors"
                title="Print Calculation Sheet"
              >
                <Printer className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 4. METHODOLOGY SUMMARY & FORMULA */}
      <section className="p-6 sm:p-8 bg-white border border-[#E5E7EB] rounded-2xl space-y-4 shadow-xs">
        <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            <h3 className="text-base font-bold text-[#1A1A1A]">
              How this calculation works ({tool.name})
            </h3>
          </div>
          <span className="text-xs text-[#6B7280]">{tool.methodology.jurisdictionDate}</span>
        </div>

        <div className="grid md:grid-cols-2 gap-6 text-xs text-[#6B7280]">
          <div className="space-y-2">
            <div className="font-bold text-[#1A1A1A]">Data Source & Statutory Authority</div>
            <p className="leading-relaxed">{tool.methodology.source}</p>
            <div className="font-bold text-[#1A1A1A] pt-2">Mathematical Formulation</div>
            <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] font-mono text-[#1A1A1A]">
              {tool.methodology.formulaExplanation}
            </div>
          </div>

          <div className="space-y-2">
            <div className="font-bold text-[#1A1A1A]">Baseline Calculation Assumptions</div>
            <ul className="space-y-1.5 list-disc list-inside">
              {tool.methodology.assumptions.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 5. ETHICAL AD SLOT */}
      <AdSlot />

      {/* 6. RELATED TOOLS */}
      {relatedTools.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-3">
            <h3 className="text-base font-bold text-[#1A1A1A]">
              Related Tools in {tool.category}
            </h3>
            <button
              onClick={() => onNavigate('tools')}
              className="text-xs font-bold text-[#1A1A1A] hover:underline flex items-center gap-1"
            >
              <span>View all tools</span>
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {relatedTools.map((rt) => (
              <button
                key={rt.id}
                onClick={() => onSelectTool(rt.id)}
                className="p-4 text-left bg-white hover:bg-[#FBFBFC] border border-[#E5E7EB] hover:border-[#1A1A1A] rounded-xl transition-all space-y-1.5 group"
              >
                <div className="text-xs font-bold text-[#1A1A1A] group-hover:text-black">
                  {rt.name}
                </div>
                <p className="text-[11px] text-[#6B7280] line-clamp-2">
                  {rt.tagline}
                </p>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Methodology Drawer Modal */}
      <MethodologyDrawer
        isOpen={methodologyOpen}
        onClose={() => setMethodologyOpen(false)}
        toolName={tool.name}
        source={tool.methodology.source}
        formulaExplanation={tool.methodology.formulaExplanation}
        jurisdictionDate={tool.methodology.jurisdictionDate}
        assumptions={tool.methodology.assumptions}
      />
    </div>
  );
};
